import EndpointDetailTable from "components/ui/EndpointDetailTable";
import { useState, useEffect, useMemo, useCallback } from "react";
import { useParams } from "react-router-dom";
import usePageTitle from "system/hooks/use-page-title";
import ITitleData from "system/types/interfaces/ITitleData";
import IMachineDetails from "system/types/interfaces/IMachineDetails";
import ComplianceStatus from "components/ui/ComplianceStatus";
import EndpointSubjectStatusList from "components/ui/EndpointSubjectStatusList";
import LoadingPage from "./system/LoadingPage";
import { ComplianceStatusToString, getFactValue, getUtcOffset, toDate, toFormatedDate, toLocalISOString } from "system/libraries/utilities";
import ActionButtons from "components/ui/ActionButtons";
import useChartRange from "system/hooks/use-chart-range";
import usePageNavigation from "system/hooks/use-page-navigation";
import useApi from "system/hooks/use-api";
import useBreadcrumbs from "system/hooks/use-breadcrumbs";
import IAppBreadcrumb from "system/types/interfaces/IAppBreadcrumb";
import ExportToCsvButton from "components/ui/ExportToCsvButton";
import { descriptionByFactCode } from "system/libraries/constants";
import { useUrlSearch } from "system/hooks";
import TheMainChart from "components/ui/TheMainChart";
import { SubjectStatusTime } from "system/types/wireTypes";
import { RetryErrorPage } from "./system";
import { useDispatch, useSelector } from "react-redux";
import IRootState from "system/types/store/IRootState";
import { mainActions } from "system/store/slices/main-slice";
import { IIconData } from "system/types/interfaces/IIconData";

const factsToExport = [
  "COMPUTERNAME",
  "ADSITE",
  "OSARCH",
  "BIOSVER",
  "CONFIG",
  "MFR",
  "MODEL",
  "BOARDSER",
  "OSNAME",
  "OSVER",
  "REPO",
  "WINACCT",
];

const EndpointPage = () => {
  const api = useApi();
  const { machineId } = useParams();
  const { getEndpointPageUrl, navigateToEndpointReportHistoryPage , getHomePageUrl } = usePageNavigation();
  const [refreshCount, setRefreshCount] = useState(0);
  const [machineData, setMachineData] = useState<IMachineDetails | null>(null);
  const [chartData, setChartData] = useState<SubjectStatusTime[] | null>(null);
  const [chartError, setChartError] = useState<string | null>(null);
  const [gridError, setGridError] = useState<string | null>(null);

  const [titleData, setTitleData] = useState<ITitleData>({
    title: "Endpoint Information",
    subTitle: "",
  });
  const { icons } = useSelector((state: IRootState) => state.main);

  usePageTitle(titleData);
  const dispatch = useDispatch();

  const urlSearch = useUrlSearch();
  const dates = useChartRange();

  const getSelectedDateString = useCallback((): string | null => {
    const paramDate = urlSearch.get("selectedDate");

    if (paramDate) {
      return paramDate;
    }

    if (dates?.length) {
      const resultDate = toLocalISOString(dates[dates.length - 1]);
      return resultDate;
    }

    return null;
  }, [urlSearch, dates]);

  const time = useMemo(() => getSelectedDateString(), [getSelectedDateString]);

  useBreadcrumbs(
    useMemo<IAppBreadcrumb[]>(() => {
      let result: IAppBreadcrumb[] = [];
      if (machineData?.machineName) {
        result.push({
          name: machineData.machineName,
          url: time ? getEndpointPageUrl(machineId, time) : "",
        });

        if (time) {
          result.push({
            title: "Time",
            name: toFormatedDate(time),
            url: getHomePageUrl(time),
          });
        }
      }

      return result;
    }, [time, machineData, getEndpointPageUrl, machineId])
  );

  const loadMainChart = useCallback(() => {
    setChartError(null);
    if (!dates) {
      setChartData(null);
      return;
    }

    const controller = new AbortController();
    const fetchData = async () => {
      try {
        const data = await api.summary.subjectStatus(
          {
            breakoutOption: 0,
            filters: {
              adSite: null,
              computerName: null,
              configurationName: null,
              hardwareMfr: null,
              hardwareModel: null,
              subject: null,
              osname: null,
            },
            graphBy: "subject",
            machine: machineId,
            referenceTimes: dates.map((x: Date) => x.toISOString()),
          },
          {
            retries: 3,
            baseDelay: 1000,
            maxDelay: 10000,
            signal: controller.signal,
          }
        );
        if (data && data.isSuccess && data?.byBreakoutValue && data.byBreakoutValue.length > 0) {
          setChartData(data?.byBreakoutValue[0].byTime);
          setChartError(null);
        } else {
          setChartError(data?.failMessage || "Failed to load subject history after multiple attempts.");
        }
      } catch (error: any) {
        if (error.name === "AbortError") return;
        console.error("Error fetching subject history:", error);
        setChartError(error.message || "Failed to load subject history after multiple attempts.");
      }
    };

    fetchData();

    return () => {
      controller.abort();
      setChartData(null);
      setChartError(null);
    };
  }, [api.summary, dates, machineId]);

  useEffect(() => {
    if (!dates) {
      return;
    }

    let requestTime: Date | null = null;

    if (time && !isNaN(Date.parse(time))) {
      requestTime = new Date(time);
    }

    if (!!machineId && !isNaN(+machineId)) {
      setGridError(null);
      const controller = new AbortController();
      const fetchData = async () => {
        try {
          const data = await api.machine.getMachineDetail(+machineId, requestTime, dates, {
            retries: 3,
            baseDelay: 1000,
            maxDelay: 10000,
            signal: controller.signal,
          });
          if (data && data.isSuccess) {
            if (data.value && icons.length < data.value.subjectStatuses.length) {
              const iconsList = await api.icons.getIconsBySubjects(data.value.subjectStatuses.map((x) => x.subject));
              if (iconsList && iconsList.value) {
                const icons: IIconData[] = iconsList.value.map((item) => ({
                  subject: item.packageName,
                  iconUrl: item.iconUrl,
                }));

                dispatch(mainActions.setIconData(icons));
              }
            }
            setMachineData(data.value);
            const machineName = data.value?.machineName;
            loadMainChart();

            if (machineName) {
              setTitleData({
                title: machineName,
                subTitle: "Endpoint",
                navigateOnTitleClick: time ? getEndpointPageUrl(machineId) : "",
              });
            }
            setGridError(null);
          } else {
            setGridError(data?.failMessage || "Failed to load endpoint data after multiple attempts.");
          }
        } catch (error: any) {
          if (error.name === "AbortError") return;
          console.error("Error fetching endpoint data:", error);
          setGridError(error.message || "Failed to load endpoint data after multiple attempts.");
        }
      };

      fetchData();

      return () => {
        controller.abort();
        setMachineData(null);
        setGridError(null);
      };
    }

    return () => {
      setGridError(null);
    };
  }, [machineId, dates, time, refreshCount, api, getEndpointPageUrl, loadMainChart, icons.length, dispatch]);

  const exportData = useMemo(
    () => [
      {
        text: "Asset data",
        fileName: `Aiden ${machineData?.machineName} Endpoint Asset Data ${toDate(machineData?.machineAtTime)}`,
        source: factsToExport
          .map((x) => ({
            Property: descriptionByFactCode[x],
            Value: getFactValue(machineData?.machineFacts, x),
          }))
          .concat([
            {
              Property: "Last recorded",
              Value: toFormatedDate(machineData?.subjectStatuses?.length ? machineData?.subjectStatuses[0]?.lastReportedJsTime : ""),
            },
            {
              Property: "Last recorded ISO8601",
              Value: machineData?.subjectStatuses?.length ? machineData?.subjectStatuses[0]?.lastReportedJsTime : "",
            },
          ]),
      },
      {
        text: "Compliance data",
        fileName: `Aiden ${machineData?.machineName} Endpoint Compliance Data ${toDate(machineData?.machineAtTime)}`,
        source: machineData?.subjectStatuses?.map((x) => ({
          Subject: x.subject,
          Package: x.packageName,
          State: ComplianceStatusToString(x.complianceStatus),
          Justification: x.complianceJustification,
          AssessmentTime: toFormatedDate(x.referenceJsTime),
          AssessmentTimeISO8601: x.referenceJsTime,
          UseCase: x.packageType,
          Rule: x.ruleDescription,
          LastExitCode: x.packageExitCode,
          Status: x.applicabilityMessage,
        })),
      },
    ],
    [machineData]
  );

  const refreshHandler = () => {
    setMachineData(null);
    loadMainChart();
    setGridError(null);
    setChartError(null);
    setRefreshCount(refreshCount + 1);
  };

  const openReportHistoryHandler = () => {
    navigateToEndpointReportHistoryPage(machineId);
  };

  const complianceStatus = useMemo(() => {
    if (!machineData?.machineFacts) return null;
    return machineData.machineFacts?.find((x) => x.code === "COMPLIANCESTATUS");
  }, [machineData?.machineFacts]);

  let complianceJustification = useMemo(() => {
    if (!machineData?.machineFacts) return "No reporting data";
    getFactValue(machineData.machineFacts, "COMPLIANCEJUSTIFICATION");
  }, [machineData?.machineFacts]);

  if (!chartData && !chartError && !machineData && !gridError) return <LoadingPage />;

  return (
    <>
      <div style={{ paddingBottom: "10px" }}>
        <ComplianceStatus code={+(complianceStatus?.value ?? 0)} isUnknown={complianceStatus?.isUnknown ?? true} />
        <div style={{ paddingLeft: "10px", marginTop: "10px" }} className="ts-date-title">
          {complianceJustification}
        </div>
      </div>
      <ActionButtons
        timespanButton={true}
        onRefresh={refreshHandler}
        items={[
          {
            title: "Report history",
            action: openReportHistoryHandler,
            icon: "Clock",
          },
        ]}
      >
        <ExportToCsvButton items={exportData} />
      </ActionButtons>

      <div className="machine-detail-chart">
        {chartError ? (
          <RetryErrorPage errorMessage="Failed to load chart data" refreshHandler={loadMainChart} />
        ) : chartData ? (
          <TheMainChart data={chartData} selectedGraphBy="subject" />
        ) : (
          <LoadingPage />
        )}
        <div className="summary-number" style={{ marginTop: "0px" }}>
          <div className="d-flex justify-content-between gap-3 align-items-center">
            <p className="mb-0">
              <span className="endpoint-head">Endpoint information</span>
            </p>
            <p className="d-flex flex-column flex-md-row gap-1 gap-md-2 align-items-md-center mb-0">
              <span className="lh-sm"> Selected date and time</span>
              <span className="number lh-sm">{machineData && toFormatedDate(machineData.machineAtTime.toString())} (Browser local time UTC{getUtcOffset(new Date(machineData?.machineAtTime ?? ""))})</span>
            </p>
          </div>
        </div>

        {chartError ? (
          <RetryErrorPage errorMessage="Failed to load grid data" refreshHandler={loadMainChart} />
        ) : machineData ? (
          <EndpointDetailTable machineDetails={machineData} />
        ) : (
          <LoadingPage />
        )}
      </div>
      <div className="machine-table scroll" style={{ maxHeight: "70vh" }}>
        {chartError ? (
          <RetryErrorPage errorMessage="Failed to load grid data" refreshHandler={loadMainChart} />
        ) : machineData ? (
          <EndpointSubjectStatusList
            subjectStatuses={machineData.subjectStatuses}
            time={machineData.machineAtTime.toString()}
            machineId={machineId}
          />
        ) : (
          <LoadingPage />
        )}
      </div>
    </>
  );
};

export default EndpointPage;
